import styled from 'styled-components';
import { Spacing4, Spacing24, Spacing32 } from 'autogen/design-tokens/tokens';
import type { ProductPageViewModel } from 'autogen/swagger/all';
import { useGetProductInformationQuery } from 'autogen/swagger/all/hooks';
import Layout from 'components/Layout';
import ProductAccordion from 'components/product/accordion';
import OTCNotification from 'components/product/components/OTCNotification';
import ProductItemGallery from 'components/product/ProductItemGallery/ProductItemGallery';
import { breakpoint, sizes } from 'variables';
import HazardLabel from './MidSection/components/HazardLabel';
import SplashTagLines from './MidSection/components/SplashTagLines';
import MidSection from './MidSection/MidSection';
import ProductChips from './ProductChips';

const Container = styled.div`
    display: flex;
    row-gap: ${Spacing24};
    flex-direction: column;

    ${breakpoint.up(sizes.lg)} {
        justify-content: space-between;
        column-gap: ${Spacing32};
        flex-direction: row;
    }
    ${breakpoint.up(sizes.xl)} {
        column-gap: 8.5%;
    }
`;

const ImageColumn = styled(Layout.Flex.Column)`
    ${breakpoint.up(sizes.lg)} {
        position: relative;
        flex: 1 1 56.5%;
    }
`;

const Sticky = styled(Layout.Flex.Column)`
    ${breakpoint.up(sizes.lg)} {
        position: sticky;
        top: ${Spacing32};
    }
`;

const InformationColumn = styled(Layout.Flex.Column)`
    ${breakpoint.up(sizes.lg)} {
        min-width: 350px;
        flex: 1 1 35%;
    }
`;

const StyledSplashTagLines = styled(SplashTagLines)`
    margin-bottom: ${Spacing4};

    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

type ProductInformationProps = Pick<ProductPageViewModel, 'productInformation' | 'emaLogo'> & {
    chips?: ProductPageViewModel['chips'];
    canSmartBundleBeRequested: boolean;
};

export default function ProductInformation({
    productInformation,
    canSmartBundleBeRequested,
    emaLogo,
    chips,
    ...restProps
}: ProductInformationProps) {
    const { data: fetchedModel } = useGetProductInformationQuery(
        {
            id: productInformation.productId,
            subscription: !!productInformation.expectedDelivery?.subscriptionPriceInfoViewModel,
            showStoreStock: productInformation.showStoreStock,
            showVto: productInformation.revieveVirtualTryOn.showVto
        },
        {
            initialData: productInformation,
            refetchOnWindowFocus: false,
            refetchOnMount: false
        }
    );

    const model = fetchedModel || productInformation;
    const { productMedia, splashes, otcWarning, hazarlabelUrls } = model;

    return (
        <Container {...restProps}>
            {/* Product Image */}
            <ImageColumn>
                <Sticky gap={{ xs: Spacing24 }}>
                    <div>
                        <StyledSplashTagLines splashes={splashes} />

                        <ProductItemGallery items={productMedia} splashes={splashes} emaLogo={emaLogo} />
                    </div>

                    {otcWarning && <OTCNotification model={otcWarning} />}

                    {!!hazarlabelUrls.length && <HazardLabel model={hazarlabelUrls} />}
                </Sticky>
            </ImageColumn>

            {/* Product info + Buying Options + Product accordion */}
            <InformationColumn gap={{ xs: Spacing24, lg: Spacing32 }}>
                <MidSection model={model} canSmartBundleBeRequested={canSmartBundleBeRequested} />
                <ProductChips chips={chips} productId={model.productId} />
                <ProductAccordion model={model} />
            </InformationColumn>
        </Container>
    );
}
